import { Box, Typography } from '@mui/material';
import { useTheme } from '@/styles/use-finance-theme';
import { Link as RouterLink } from 'react-router-dom';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { useSession } from '../../features/auth/session-provider';
import { BRAND_NAME, BRAND_SHORT_NAME } from '../../config/brand';

export interface ShellBrandProps {
  dashboardPath: string;
}

export function ShellBrand({ dashboardPath }: ShellBrandProps) {
  const theme = useTheme();
  const { currentContext } = useSession();
  const { brandWidth, headerHeight, compactHeaderHeight } = theme.financeLayout.shell;

  return (
    <Box
      component={RouterLink}
      to={dashboardPath}
      aria-label={BRAND_NAME}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 0.5, sm: 1 },
        mr: { xs: 0.5, sm: 1.5 },
        flexShrink: 0,
        width: { xs: 'auto', sm: brandWidth },
        minHeight: { xs: compactHeaderHeight, sm: headerHeight },
        textDecoration: 'none',
        color: 'inherit',
        borderRadius: 1,
        '&:hover': { opacity: 0.85 },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 2,
        },
      }}
      data-testid="shell-branding"
    >
      <AccountBalanceIcon color="primary" fontSize="medium" />
      <Box
        sx={{
          display: { xs: 'none', sm: 'flex' },
          flexDirection: 'column',
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <Typography variant="subtitle1" component="span" sx={{ fontWeight: 700 }} noWrap>
          {BRAND_NAME}
        </Typography>
        {currentContext?.organizationName && (
          <Typography
            variant="caption"
            component="span"
            color="text.secondary"
            noWrap
            sx={{
              display: { sm: 'none', md: 'block' },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {currentContext.organizationName}
          </Typography>
        )}
      </Box>
      <Typography
        variant="subtitle1"
        component="span"
        sx={{ fontWeight: 700, display: { xs: 'block', sm: 'none' } }}
        noWrap
      >
        {BRAND_SHORT_NAME}
      </Typography>
    </Box>
  );
}
